import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faBirthdayCake } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import type { To } from 'react-router';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { WithScrollButtons } from '@sorare/core/src/atoms/navigation/WithScrollButtons';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { FlagAvatar } from '@sorare/core/src/components/country/FlagAvatar';
import { PositionIcon } from '@sorare/core/src/components/lineup/PositionIcon';
import { PlayerHandsInfo } from '@sorare/core/src/components/player/PlayerHandsInfo';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  ANY_SPORT_MARKET_SCOUT,
  ANY_SPORT_TEAMS_SLUG,
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_MARKET_SCOUT_SO5_COMPETITION_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { battingThrowingHandMessage } from '@sorare/core/src/lib/baseball';
import { isType } from '@sorare/core/src/lib/gql';
import { positionNames } from '@sorare/core/src/lib/players';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { U23Eligible } from '@sorare/marketplace/src/components/card/U23Eligible';
import { InjuryTag } from '@sorare/marketplace/src/components/player/InjuryTag';

import type { AnyPlayerHeaderRow_anyPlayer } from './__generated__/index.graphql.ts';

const StyledFullViewportScroll = styled(FullViewportScroll)`
  align-items: center;
  gap: var(--unit);
  @media ${laptopAndAbove} {
    gap: var(--double-unit);
  }
  & > * {
    flex-shrink: 0;
  }
`;

const Img = styled(Horizontal).attrs({
  center: true,
})`
  width: 24px;
  height: 24px;
`;

const So5Logo = styled.img`
  width: 24px;
  height: 24px;
`;

const StyledLabelM = styled(LabelM)`
  display: flex;
  align-items: center;
  gap: var(--unit);
`;

const Item = ({
  icon,
  label,
  withoutDivider = false,
  to,
}: {
  icon: ReactNode;
  label: string | ReactNode;
  withoutDivider?: boolean;
  to?: To;
}) => {
  return (
    <>
      {!withoutDivider && <VerticalDivider />}
      <ConditionalWrapper
        wrap={!!to}
        Wrapper={(p: { children: ReactNode }) => (
          <Link to={to!}>{p.children}</Link>
        )}
      >
        <Vertical className="text-center" center>
          <Img>{icon}</Img>
          <LabelM>{label}</LabelM>
        </Vertical>
      </ConditionalWrapper>
    </>
  );
};

type Props = {
  anyPlayer: AnyPlayerHeaderRow_anyPlayer;
};

export const AnyPlayerHeaderRow = ({ anyPlayer }: Props) => {
  const scrollContainerRef = useRef(null);
  const wrapperRef = useRef(null);

  const { cardPositions, shirtNumber } = anyPlayer;
  const mainPosition =
    cardPositions?.[0] || ('position' in anyPlayer && anyPlayer?.position);

  return (
    <WithScrollButtons
      contained
      scrollContainerRef={scrollContainerRef}
      wrapperRef={wrapperRef}
    >
      <StyledFullViewportScroll ref={scrollContainerRef}>
        <Item
          withoutDivider
          icon={
            <FlagAvatar
              country={anyPlayer.country}
              withCountryFlag
              type="flat"
              size={24}
            />
          }
          label={anyPlayer.country.threeLetterCode?.toUpperCase()}
        />
        <Item
          icon={
            'u23Eligible' in anyPlayer && anyPlayer?.u23Eligible ? (
              <U23Eligible until={anyPlayer.u23EligibleUntil} />
            ) : (
              <FontAwesomeIcon icon={faBirthdayCake} size="lg" />
            )
          }
          label={
            <FormattedMessage
              id="PlayerHeaderRow.age"
              defaultMessage="{age} yo"
              values={{
                age: anyPlayer.age,
              }}
            />
          }
        />
        <ConditionalWrapper
          wrap={cardPositions.length > 1}
          Wrapper={(p: { children: ReactNode }) => (
            <Tooltip
              title={
                <Vertical>
                  {cardPositions.map(position => (
                    <StyledLabelM key={position}>
                      <PositionIcon size={2} position={position} />
                      <FormattedMessage {...positionNames[position]} />
                    </StyledLabelM>
                  ))}
                </Vertical>
              }
            >
              {p.children}
            </Tooltip>
          )}
        >
          <Item
            icon={<PositionIcon size={3} position={mainPosition} />}
            to={`${generateSportPath(ANY_SPORT_MARKET_SCOUT, {
              sport: anyPlayer.sport,
            })}?${buildFilterQuery({
              [SEARCH_PARAMS.POSITION]: mainPosition,
            })}`}
            label={
              <>
                <FormattedMessage {...positionNames[mainPosition]} />
                {cardPositions.length > 1 && `, +${cardPositions.length - 1}`}
              </>
            }
          />
        </ConditionalWrapper>
        {!!anyPlayer.activeClub && (
          <Item
            icon={<TeamAvatar size={24} team={anyPlayer.activeClub} />}
            label={anyPlayer.activeClub.shortName}
            to={generateSportPath(ANY_SPORT_TEAMS_SLUG, {
              params: { slug: anyPlayer.activeClub.slug },
              sport: anyPlayer.sport,
            })}
          />
        )}
        {isType(anyPlayer, 'Player') && !!anyPlayer.activeNationalTeam && (
          <Item
            icon={<TeamAvatar size={24} team={anyPlayer.activeNationalTeam} />}
            label={anyPlayer.activeNationalTeam.officialName}
            to={generatePath(FOOTBALL_COUNTRIES_COUNTRYCODE, {
              countryCode: anyPlayer.country.slug,
            })}
          />
        )}
        {isType(anyPlayer, 'BaseballPlayer') && (
          <Item
            icon={<PlayerHandsInfo player={anyPlayer} />}
            label={<FormattedMessage {...battingThrowingHandMessage.header} />}
          />
        )}

        {isType(anyPlayer, 'Player') &&
          anyPlayer.eligibleSo5Competitions.map(competition => (
            <Item
              key={competition.slug}
              icon={
                <So5Logo
                  src={competition.iconUrl}
                  alt={competition.displayName}
                />
              }
              label={competition.displayName}
              to={generatePath(FOOTBALL_MARKET_SCOUT_SO5_COMPETITION_SLUG, {
                slug: competition.slug,
              })}
            />
          ))}

        <Item
          icon={<BrandM>#{shirtNumber}</BrandM>}
          label={
            <FormattedMessage
              id="PlayerHeaderRow.shirtNumber"
              defaultMessage="Shirt number"
            />
          }
        />

        {!isType(anyPlayer, 'Player') && !!anyPlayer?.activeInjuries[0] && (
          <Item
            icon={<InjuryTag player={anyPlayer} />}
            label={
              <FormattedMessage
                id="PlayerHeaderRow.injuryStatus"
                defaultMessage="Injury status"
              />
            }
          />
        )}
      </StyledFullViewportScroll>
    </WithScrollButtons>
  );
};

AnyPlayerHeaderRow.fragments = {
  anyPlayer: gql`
    fragment AnyPlayerHeaderRow_anyPlayer on AnyPlayerInterface {
      slug
      sport
      country {
        slug
        name
        threeLetterCode
      }
      activeClub {
        slug
        shortName
        ...TeamAvatar_team
      }
      activeNationalTeam {
        slug
        officialName
        ...TeamAvatar_team
      }
      age
      cardPositions
      eligibleSo5Competitions {
        slug
        iconUrl
        displayName
      }
      shirtNumber
      ... on Player {
        slug
        u23Eligible
        u23EligibleUntil
        position
      }
      ...PlayerHandsInfo_AnyPlayerInterface
      ...InjuryTag_anyPlayer
    }
    ${PlayerHandsInfo.fragments.AnyPlayerInterface}
    ${TeamAvatar.fragments.team}
    ${InjuryTag.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyPlayerHeaderRow_anyPlayer>,
};
